import { useMemo } from 'react'

import { Formik, Form } from 'formik'
import { string, object, number, SchemaOf } from 'yup'

import { Collapse } from '@mui/material'

import { useSelector } from '@tabeeb/store'
import { TenantPermission } from '@tabeeb/enums'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from '@collaborate-health/uikit'
import {
  maxLength,
  noWhitespaces,
  requiredField,
  FormSelect,
  FormTextField,
  FormAutocomplete,
} from '@collaborate-health/forms'
import {
  CertificateCategory,
  CertificateTypeCreateRequest,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { hasTenantPermission } from '@tabeeb/modules/permissions/selectors'

import { useCurrentCorporation } from '@tabeeb/modules/admin/corporations/hooks'
import {
  CertificateScopeType,
  CertificateScopeTypeDisplayName,
  PeriodUnitTypeDisplayName,
  CertificateTypeVisibilityScopeDisplayName,
  PeriodUnitType,
  CertificateTypeVisibilityScope,
} from '../../enums'
import { CertificateType } from '../../types'

const validationSchema: SchemaOf<CertificateTypeCreateRequest> = object().shape(
  {
    Name: string().strict().trim(noWhitespaces).required(requiredField).max(100, maxLength(100)),
    CategoryId: number().nullable().required(requiredField),
    VisibilityScope: number().optional().nullable().required(requiredField),
    ScopeId: number().nullable().required(requiredField),
    DefaultValidityPeriod: number()
      .optional()
      .nullable()
      .min(1, 'Minimum is 1')
      .when('DefaultValidityPeriodUnitId', {
        is: (unitId: number) => unitId,
        then: number().optional().nullable().min(1, 'Minimum is 1').required(requiredField),
      }),
    DefaultValidityPeriodUnitId: number()
      .optional()
      .when('DefaultValidityPeriod', {
        is: (period: number) => Boolean(period),
        then: number().required(requiredField),
      }),
  },
  [['DefaultValidityPeriodUnitId', 'DefaultValidityPeriod']]
)

export interface AddEditCertificateTypeDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (args: { values: CertificateTypeCreateRequest; editMode: boolean }) => void
  onExited: () => void
  initial?: CertificateType | null
  categories: CertificateCategory[]
}

const AddEditCertificateTypeDialog: React.FC<AddEditCertificateTypeDialogProps> = ({
  open,
  initial,
  categories,
  onClose,
  onSubmit,
  onExited,
}) => {
  const editMode = Boolean(initial?.Id)
  const { canManageCorporationCertificates } = useCurrentCorporation()

  const hasManageCertificatesGlobalScopePermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.CertificatesGlobalScope)
  )

  const certificateCategoryOptions = useMemo(
    () =>
      categories.map((value) => ({
        name: value.Name,
        value: value.Id,
      })),
    [categories]
  )

  const availableVisibilityScopesPerCertificateScope = useMemo<
    Record<CertificateScopeType, CertificateTypeVisibilityScope[]>
  >(() => {
    const map: Record<CertificateScopeType, CertificateTypeVisibilityScope[]> = {
      [CertificateScopeType.ProfileCertificate]: [CertificateTypeVisibilityScope.Tenant],
      [CertificateScopeType.TenantCertificate]: [],
      [CertificateScopeType.UserCertificate]: [],
    }

    if (hasManageCertificatesGlobalScopePermission) {
      map[CertificateScopeType.TenantCertificate].push(CertificateTypeVisibilityScope.System)
      map[CertificateScopeType.UserCertificate].push(CertificateTypeVisibilityScope.System)
    }

    if (canManageCorporationCertificates) {
      map[CertificateScopeType.UserCertificate].push(CertificateTypeVisibilityScope.Corporation)
    }

    return map
  }, [canManageCorporationCertificates, hasManageCertificatesGlobalScopePermission])

  const certificateTypeScopeOptions = useMemo(
    () =>
      [
        CertificateScopeType.ProfileCertificate,
        CertificateScopeType.TenantCertificate,
        CertificateScopeType.UserCertificate,
      ]
        .filter((value) => {
          if (editMode) {
            return true
          }

          return availableVisibilityScopesPerCertificateScope[value].length > 0
        })
        .map((value) => ({
          name: `${CertificateScopeTypeDisplayName[value]}`,
          value,
        })),
    [editMode, availableVisibilityScopesPerCertificateScope]
  )

  const periodTypeOptions = useMemo(
    () =>
      [PeriodUnitType.Day, PeriodUnitType.Month, PeriodUnitType.Year].map((unitType) => ({
        name: PeriodUnitTypeDisplayName[unitType],
        value: unitType,
      })),
    []
  )

  const visibilityScopeOptions = useMemo(
    () =>
      [
        CertificateTypeVisibilityScope.System,
        CertificateTypeVisibilityScope.Corporation,
        CertificateTypeVisibilityScope.Tenant,
      ].map((scope) => ({
        name: CertificateTypeVisibilityScopeDisplayName[scope],
        value: scope,
      })),
    []
  )

  const initialValues = useMemo<CertificateTypeCreateRequest>(
    () => ({
      Id: initial?.Id,
      Name: initial?.Name || '',
      CategoryId: initial?.CategoryId ?? null!,
      CorporationId: initial?.CorporationId || null,
      TenantId: initial?.TenantId || null,
      ScopeId: initial?.ScopeId ?? null!,
      VisibilityScope: initial?.VisibilityScope ?? CertificateTypeVisibilityScope.Tenant,
      DefaultValidityPeriod: initial?.DefaultValidityPeriod || null,
      DefaultValidityPeriodUnitId: initial?.DefaultValidityPeriodUnitId ?? undefined,
    }),
    [
      initial?.CategoryId,
      initial?.CorporationId,
      initial?.DefaultValidityPeriod,
      initial?.DefaultValidityPeriodUnitId,
      initial?.Id,
      initial?.Name,
      initial?.ScopeId,
      initial?.TenantId,
      initial?.VisibilityScope,
    ]
  )

  return (
    <Dialog open={open} onClose={onClose} TransitionProps={{ onExited }} fullWidth maxWidth='sm'>
      <DialogTitle>{editMode ? 'Edit certificate type' : 'Create certificate type'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => onSubmit({ values, editMode })}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ values, isValid, dirty, setFieldValue }) => (
          <Form autoComplete='off'>
            <DialogContent>
              <Stack spacing={2}>
                <FormTextField name='Name' label='Name' required />
                <FormSelect
                  name='CategoryId'
                  label='Category'
                  required
                  options={certificateCategoryOptions}
                  disabled={editMode}
                />
                <FormAutocomplete
                  name='ScopeId'
                  label='Scope'
                  required
                  options={certificateTypeScopeOptions}
                  disabled={editMode}
                  freeSolo={false}
                  onChange={(e, option) => {
                    if (!option) {
                      return
                    }

                    setFieldValue('VisibilityScope', availableVisibilityScopesPerCertificateScope[option.value][0])
                  }}
                />
                <Collapse
                  in={values.ScopeId && availableVisibilityScopesPerCertificateScope[values.ScopeId].length > 1}
                  unmountOnExit
                >
                  <FormAutocomplete
                    name='VisibilityScope'
                    label='Visibility Scope'
                    required
                    options={visibilityScopeOptions.filter((option) =>
                      (availableVisibilityScopesPerCertificateScope[values.ScopeId] ?? []).includes(option.value)
                    )}
                    disabled={editMode}
                  />
                </Collapse>
                <Stack direction='row' gap={2}>
                  <FormTextField
                    type='number'
                    name='DefaultValidityPeriod'
                    label='Default Validity Period'
                    disabled={editMode}
                  />
                  <FormSelect
                    name='DefaultValidityPeriodUnitId'
                    label='Period Type'
                    options={periodTypeOptions}
                    disabled={editMode}
                  />
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button color='primary' onClick={onClose}>
                Cancel
              </Button>
              <Button variant='contained' color='primary' type='submit' disabled={!(isValid && dirty)}>
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default AddEditCertificateTypeDialog

